import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/site";
import { TESTIDS } from "@/constants/testIds/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="glass-header sticky top-0 z-40 w-full">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="flex h-[72px] items-center justify-between">
          <Link
            to="/"
            data-testid={TESTIDS.headerLogo}
            className="flex items-center gap-3"
          >
            <span className="kbd-rule" />
            <span className="font-serif text-[22px] leading-none tracking-tight text-[var(--text-main)]">
              Weston <span className="italic text-[var(--primary)]">Commercial</span>
            </span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-9"
            data-testid={TESTIDS.headerNav}
          >
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={TESTIDS.headerNavLink(l.label.toLowerCase().replace(/\s+/g, "-"))}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-[var(--primary)]"
                      : "text-[var(--text-main)] hover:text-[var(--primary)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="flex items-center gap-2 text-[12px] font-medium text-[var(--text-muted)] hover:text-[var(--primary)] transition"
            >
              <Phone size={14} strokeWidth={1.5} />
              {BRAND.phone}
            </a>
            <Link
              to="/contact"
              data-testid={TESTIDS.headerCta}
              className="bg-[var(--ink)] text-white px-5 py-3 text-[11px] font-extrabold tracking-[0.1em] uppercase rounded-[4px] border-2 border-[var(--ink)] transition hover:bg-[var(--primary)] hover:border-[var(--primary)]"
            >
              Schedule a Tour
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={TESTIDS.headerMobileToggle}
            className="lg:hidden p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-black/5 bg-[var(--bg)]"
          data-testid={TESTIDS.headerMobileMenu}
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 border-b border-[var(--border)] text-[15px] ${
                    isActive ? "text-[var(--primary)]" : "text-[var(--text-main)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-3 mt-5">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="btn-ghost justify-center"
              >
                <Phone size={14} strokeWidth={1.5} />
                {BRAND.phone}
              </a>
              <Link to="/contact" className="btn-primary justify-center">
                Schedule a Tour
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
